import {
  getPackageMajorVersion,
  createSucceededResult,
  wrappedImport,
} from "@amzn/aws-console-lotus-web-client-common";

export const LINK_ATTRIBUTE = "data-lotus-link-package";
export const SCRIPT_ATTRIBUTE = "data-lotus-script-package";

export const createResourceUrl = ({
  basePath,
  relativePath,
  bustCache = false,
}: {
  basePath: string;
  relativePath: string;
  bustCache?: boolean;
}): string => {
  let resolvedBasePath = basePath;
  if (!resolvedBasePath.endsWith("/")) {
    resolvedBasePath = `${basePath}/`;
  }

  let resolvedRelativePath = relativePath;
  if (resolvedRelativePath.startsWith("/")) {
    resolvedRelativePath = resolvedRelativePath.slice(1);
  }

  const resolvedUrl = new URL(`${resolvedBasePath}${resolvedRelativePath}`);
  if (bustCache) {
    resolvedUrl.searchParams.set("lotusNoCache", String(Date.now()));
  }
  return resolvedUrl.toString();
};

export const injectResources = async (input: {
  packageName: string;
  majorVersion: string;
  ownerDocument: Document;
  metadataResponse: any;
}) => {
  const { packageName, majorVersion, ownerDocument, metadataResponse } = input;
  const packageMajorVersion = getPackageMajorVersion(packageName, majorVersion);
  const { metadata, basePath } = metadataResponse;

  // Inject stylesheets first
  if (metadata.assets?.length) {
    for (const asset of metadata.assets) {
      if (asset.path?.endsWith(".css")) {
        const link = ownerDocument.createElement("link");
        link.rel = "stylesheet";
        link.href = createResourceUrl({ basePath, relativePath: asset.path });
        link.setAttribute(LINK_ATTRIBUTE, packageMajorVersion);
        ownerDocument.head.appendChild(link);
      }
    }
  }

  // Inject dependency scripts
  if (metadata.dependencies?.length) {
    for (const dep of metadata.dependencies) {
      const script = ownerDocument.createElement("script");
      script.src = dep;
      script.setAttribute(SCRIPT_ATTRIBUTE, packageMajorVersion);
      ownerDocument.head.appendChild(script);
      await new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
      });
    }
  }

  // Inject main module script with proper entry script ID
  if (metadata.module) {
    const script = ownerDocument.createElement("script");
    script.type = "module";
    const moduleUrl = createResourceUrl({
      basePath,
      relativePath: metadata.module.path,
    });
    script.src = moduleUrl;
    script.setAttribute(SCRIPT_ATTRIBUTE, packageMajorVersion);
    script.id = `lotus-entry-script-${packageMajorVersion}`;

    ownerDocument.head.appendChild(script);

    await new Promise((resolve, reject) => {
      script.onload = resolve;
      script.onerror = reject;
    });

    // Dynamically import the module and attach exports to script element
    (script as any).packageExports = await wrappedImport(script.src);
  }

  return createSucceededResult({});
};
